import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { SEO_IMAGE_HEIGHT, SEO_IMAGE_WIDTH, absoluteUrl, getSeoImageUrl, normalizeCanonicalPath } from '../utils/seo';

const keepLastHeadTag = (selector) => {
  const nodes = Array.from(document.head.querySelectorAll(selector));
  nodes.slice(0, -1).forEach((node) => node.remove());
};

const dedupeSeoHeadTags = () => {
  if (typeof document === 'undefined') return;
  keepLastHeadTag('title');
  keepLastHeadTag('meta[name="description"]');
  keepLastHeadTag('meta[name="robots"]');
  keepLastHeadTag('link[rel="canonical"]');
};

export default function SEOHead({
  title,
  description,
  canonicalUrl,
  imageUrl,
  imageAlt = 'BuscaDoutor - encontre médicos, clínicas e especialidades',
  imageWidth = SEO_IMAGE_WIDTH,
  imageHeight = SEO_IMAGE_HEIGHT,
  pageType = 'website',
  robots = 'index, follow, max-image-preview:large',
  structuredData,
}) {
  const canonical = canonicalUrl
    ? absoluteUrl(normalizeCanonicalPath(canonicalUrl.replace(/^https?:\/\/[^/]+/i, '') || '/'))
    : absoluteUrl('/');
  const image = getSeoImageUrl(imageUrl);
  const descriptionText = (description || '').slice(0, 180);
  const schemas = Array.isArray(structuredData) ? structuredData.filter(Boolean) : [structuredData].filter(Boolean);

  useEffect(() => {
    const cleanupId = window.setTimeout(dedupeSeoHeadTags, 0);
    return () => window.clearTimeout(cleanupId);
  }, [canonical, descriptionText, robots, title]);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={descriptionText} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content={pageType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={descriptionText} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:image:width" content={String(imageWidth)} />
      <meta property="og:image:height" content={String(imageHeight)} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content="BuscaDoutor" />
      <meta property="og:locale" content="pt_BR" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={descriptionText} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {schemas.map((schema, index) => (
        <script key={`structured-data-${index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
